<template>
  <!-- XOL-602/603/604: "Send invitation" / "Send reset link", the explicit button that e-mails a
       contact a single-use link to choose a password (D2). It has TWO HOMES, told apart by `home`:

         system   a super, on System Details, under Change Password
         contact  a Tower Co manager (any role, not View Only), on the Contact tab, for a contact
                  in their own org

       ContactDetail decides whether either is shown (canSendPasswordLinkSuper / -Manager). This
       component decides only what the button SAYS and whether it is enabled.

       It reads GET contacts/{id}/password-link (#14) ITSELF, and is hidden until that answers,
       and for good on any failure (a 404 is an older API). It does not depend on the account
       e-mail history list: a manager has no history, and a failed list must not take the button
       with it.

       It tells its parent what #14 said about the mail mode (`loaded`, { mailMode }) so System
       Details' "View captured e-mails" / "View account e-mails" link beside it can word itself
       without a second fetch. `mailMode` is null until #14 answers and after any failure.

       A plain block, not an el-form-item, so the inline-form leak (XOL-451) cannot reach it. -->
  <div v-if="state === 'ready'" class="password-link">
    <!-- btn-icon-gap: the design system's 5px between a FontAwesome icon and its label. -->
    <el-button
      class="btn-icon-gap"
      type="warning"
      icon="fa fa-paper-plane"
      :loading="sending"
      :disabled="!!reason || sending"
      :aria-describedby="describedBy"
      @click="confirmSend">{{label}}</el-button>

    <!-- Exactly one reason shows, under the button. #606266 on white, not .text-muted (fails
         contrast). On System Details the "no Login ID" reason is the hint ContactDetail already
         shows under the Login ID, so this component draws no second line for it. -->
    <small v-if="reasonText" :id="reasonId" class="password-link__reason">{{reasonText}}</small>
  </div>
</template>

<script>
import axios from '@/lib/axiosClient'
import { activityText } from '@/lib/requestActivity'
import { errStatus, errBody } from '@/lib/linkErrors'

// How long the API holds a second send for the same contact (one per contact per 60 s).
var COOLDOWN_SECONDS = 60

export default {
  name: 'password-link-action',

  props: {
    // The SAVED values (ContactDetail's initialForm): id, name, login, email, enabled. The
    // link goes to the saved e-mail address, and the confirm names it.
    contact: {
      type: Object,
      required: true
    },

    // 'system' (a super, System Details) | 'contact' (a manager, Contact tab).
    home: {
      type: String,
      default: 'system'
    },

    // The form holds edits that are not saved yet (E-mail, Login ID or Enabled? differ from the
    // saved record). The link would go to the OLD address.
    dirty: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      // 'loading' | 'ready' | 'hidden'
      state: 'loading',
      // The answer of GET contacts/{id}/password-link.
      account: null,
      sending: false,
      cooling: false,
      coolTimer: null,
      seq: 0
    }
  },

  computed: {
    // Anything that changes what the API would answer re-reads it: a saved Login ID, e-mail or
    // Enabled? flips has_login / has_email / enabled, and another contact is another answer.
    loadKey () {
      var c = this.contact || {}

      return [c.id, c.login, c.email, c.enabled ? 1 : 0].join('|')
    },

    purpose () {
      return this.account && this.account.next_purpose === 'invite' ? 'invite' : 'reset'
    },

    label () {
      return this.copy('action.' + this.purpose)
    },

    // Why the button is disabled, first match wins. Null = enabled.
    reason () {
      var a = this.account

      if (!a) {
        return null
      }
      if (this.dirty) {
        return 'unsaved'
      }
      if (!a.has_login) {
        return 'noLogin'
      }
      if (!a.has_email) {
        return 'noEmail'
      }
      if (!a.enabled) {
        return 'disabled'
      }
      if (!a.links_ready) {
        return 'links'
      }
      if (this.cooling) {
        return 'cooldown'
      }

      return null
    },

    // The Edit gate (ContactDetail's edit-pane): a manager with the Admin or Management role can
    // add the missing e-mail address on this very tab; any other manager cannot.
    canEditContact () {
      return this.isManager('admin|management')
    },

    reasonText () {
      var contact = this.home === 'contact'

      switch (this.reason) {
        case 'unsaved':
          return this.copy('reason.unsaved')
        case 'noLogin':
          return contact ? this.copy('reason.noLogin.contact') : ''
        case 'noEmail':
          return this.copy(contact ? (this.canEditContact ? 'reason.noEmail.contactEdit' : 'reason.noEmail.contactOther') : 'reason.noEmail.system')
        case 'disabled':
          return this.copy(contact ? 'reason.disabled.contact' : 'reason.disabled.system')
        case 'links':
          return this.copy('reason.links')
        case 'cooldown':
          return this.copy('reason.cooldown')
        default:
          return ''
      }
    },

    reasonId () {
      return 'password-link-reason-' + this._uid
    },

    // Disabled -> the button points at its reason. On System Details "no Login ID" has no line of
    // its own: it points at the hint under the Login ID.
    describedBy () {
      if (!this.reason) {
        return null
      }

      return this.reason === 'noLogin' && this.home === 'system' ? 'login-hint' : (this.reasonText ? this.reasonId : null)
    },

    // 'capture' | 'redirect' | 'live'. Anything else (an unknown value) is treated as live: it has
    // no sentence of its own.
    mailMode () {
      return this.account ? this.account.mail_mode : null
    }
  },

  watch: {
    loadKey () {
      this.load()
    }
  },

  created () {
    this.load()
  },

  beforeDestroy () {
    this.seq++
    clearTimeout(this.coolTimer)
  },

  methods: {
    copy (key, params) {
      return activityText('accountMail.' + key, params)
    },

    url () {
      return this.getEndpoint('contacts/' + this.contact.id + '/password-link', null, false)
    },

    // GET #14. A failure of any kind hides the action: with no answer there is nothing true to
    // say about the account, and a 404 is an older API that has no such endpoint.
    load () {
      var self = this
      var seq = ++this.seq

      if (!this.contact || !this.contact.id) {
        this.account = null
        this.state = 'hidden'
        this.$emit('loaded', { mailMode: null })
        return Promise.resolve()
      }

      return axios.get(this.url()).then(function (response) {
        if (seq !== self.seq) {
          return
        }

        var body = response ? response.data : null

        if (!body || typeof body !== 'object') {
          self.account = null
          self.state = 'hidden'
          self.$emit('loaded', { mailMode: null })
          return
        }

        self.account = body
        self.state = 'ready'
        self.startCooldown(body.retry_after)
        self.$emit('loaded', { mailMode: self.mailMode })
      }, function () {
        if (seq !== self.seq) {
          return
        }

        self.account = null
        self.state = 'hidden'
        self.$emit('loaded', { mailMode: null })
      })
    },

    startCooldown (seconds) {
      var self = this
      var s = Math.ceil(Number(seconds) || 0)

      if (s <= 0) {
        return
      }

      clearTimeout(this.coolTimer)
      this.cooling = true
      this.coolTimer = setTimeout(function () {
        self.cooling = false
      }, s * 1000)
    },

    // The words of the confirm. The address, the name and the Login ID are the SAVED ones.
    confirmText () {
      var c = this.contact
      var text = this.copy('confirm.message.' + this.purpose, { name: c.name, email: c.email, login: c.login })

      // The sentence is for capture and redirect hosts only; live says nothing.
      if (this.mailMode === 'capture') {
        text += ' ' + this.copy('confirm.capture')
      } else if (this.mailMode === 'redirect') {
        text += ' ' + this.copy('confirm.redirect')
      }

      return text
    },

    confirmSend () {
      var self = this

      if (this.reason || this.sending) {
        return
      }

      var purpose = this.purpose

      // Plain text, no HTML. Esc = Cancel (escCancel.js); Element returns focus to the button.
      this.$confirm(this.confirmText(), this.copy('confirm.title.' + purpose), {
        confirmButtonText: this.copy('action.' + purpose),
        cancelButtonText: this.copy('confirm.cancel'),
        type: 'warning'
      }).then(function () {
        self.send(purpose)
      }, function () {
        // Cancel, Esc or the X: nothing is sent.
      })
    },

    // POST #4. No body: the server picks the purpose (an invitation if the contact has no
    // password, else a reset), and never returns the link.
    send (purpose) {
      var self = this

      this.sending = true

      return axios.post(this.url(), {}).then(function (response) {
        self.sending = false

        var body = response && response.data ? response.data : {}
        var sent = body.purpose === 'invite' || body.purpose === 'reset' ? body.purpose : purpose
        var message = self.copy('toast.queued.' + sent, { email: body.email || self.contact.email })

        if (self.mailMode === 'capture') {
          message += ' ' + self.copy('toast.captured')
        }

        self.$message({ type: 'success', message: message })
        self.startCooldown(COOLDOWN_SECONDS)
        self.$emit('sent', { purpose: sent, log_id: body.log_id })
      }, function (err) {
        self.sending = false
        self.fail(err)
      })
    },

    // One toast per answer. The 403 is read by STATUS only: a middleware 403 and the
    // controller's carry different bodies, and the wording must not guess which.
    fail (err) {
      var status = errStatus(err)
      var code = errBody(err).code
      var contact = this.home === 'contact'
      var key = null

      if (status === 422 && code === 'no_login') {
        key = contact ? 'toast.noLogin.contact' : 'toast.noLogin.system'
      } else if (status === 422 && code === 'no_email') {
        key = contact ? (this.canEditContact ? 'toast.noEmail.contactEdit' : 'toast.noEmail.contactOther') : 'toast.noEmail.system'
      } else if (status === 422 && code === 'account_disabled') {
        key = contact ? 'toast.disabled.contact' : 'toast.disabled.system'
      } else if (status === 422 && code === 'links_not_ready') {
        key = 'toast.linksNotReady'
      } else if (status === 429) {
        key = 'toast.cooldown'
        this.startCooldown(Number(errBody(err).retry_after) || COOLDOWN_SECONDS)
      } else if (status === 403) {
        key = 'toast.forbidden'
      } else if (status === 404) {
        key = 'toast.notFound'
      }

      if (key) {
        this.$message({ type: 'warning', message: this.copy(key) })
      } else {
        this.$message({ type: 'error', message: this.copy('toast.error') })
      }

      // The 422s say the account is not what #14 last told us: read it again, so the button
      // shows the reason instead of failing the same way twice.
      if (status === 422) {
        this.load()
      }
    }
  }
}
</script>

<style scoped>
.password-link { max-width: 300px; }
.password-link__reason { display: block; margin-top: 6px; color: #606266; font-size: 12px; line-height: 1.4; }
</style>
